import { GetStageResourcesResponseResources } from "@microtica/ms-engine-sdk";
import { Alert, Avatar, Button, Col, Divider, Dropdown, Menu, notification, Row, Skeleton, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getElasticSearchService, getEnvironmentService } from "../../backend";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import { MoreOutlined, SearchOutlined, PlusCircleOutlined } from "@ant-design/icons";
import ConnectClusterModal from "../../components/modals/ConnectClusterModal";
import { ComponentConfig, Dictionary, IEnvDetails, ResourceConfigurationAndStatus } from "../../types";
import { trackQuickDeploy } from "../../backend/tracking/deployment";
// import { useRecoilState } from "recoil";
// import { currentProjectState } from "../../recoil/project";
// import { useCurrentProject } from "../../contexts/Project";
import Title from "antd/lib/typography/Title";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import GitCommitLink from "../../components/GitCommitLink";
import { handleDeployEnvironment } from "../../utils/environment/handle-deploy-environment";
import { showUndeployEnvironmentModalConfirm } from "../../utils/environment/handle-undeploy-environment";
import { fetchAwsAccountsInfo, linkAwsAccountToStage } from "../../utils/aws-accounts";
import ChooseAwsAccountModal from "../../components/modals/ChooseAwsAccountModal";
import DeployResourceModal from "../../components/modals/DeployResourceModal";
import MenuItemLabel from "../../components/MenuItemLabel";
import { trackEnvComponentRemoved } from "../../backend/tracking/environment";
import { isResourceActionEnabled } from "../../utils/environment";
import ExplanationButton from "../../components/explanations/ExplanationButton";
import ResourceExplanation from "../../components/explanations/ResourceExplanation";
import StageClustersTable from "../../components/StageClustersTable";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { getResourcesReferencingParticularResource } from "../../utils/environment/get-resource-references";

const EnvironmentInfrastructure = () => {
    const { projectId, envId } = useParams();
    const navigate = useNavigate();
    // const [currentProject, setCurrentProject] = useRecoilState(currentProjectState);
    // const { updateCurrentProject } = useCurrentProject();
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [resources, setResources] = useState<GetStageResourcesResponseResources[]>([]);
    const [stageResourcesOutputs, setStageResourcesOutputs] = useState<Dictionary<string[]>>({});
    const [loading, setLoading] = useState(true);
    const [loadingDeploy, setLoadingDeploy] = useState(false);
    const [showConnectClusterModal, setShowConnectClusterModal] = useState(false);
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    // const [shouldInitiateTrial, setShouldInitiateTrial] = useState(currentProject?.paymentPlan?.id === "FREE");
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    const [chooseAwsAccountModal, setChooseAwsAccountModal] = useState(false);
    const [awsAccountId, setAwsAccountId] = useState<string>();
    const [showDeployResourceModal, setShowDeployResourceModal] = useState<boolean>(false);
    const [resource, setResource] = useState<GetStageResourcesResponseResources & { configurations: ComponentConfig[] }>();
    const [resourcesConfigurations, setResourcesConfigurations] = useState<ResourceConfigurationAndStatus>();
    const [executeSavedAction, setExecuteSavedAction] = useState<Function>();
    const [resourceActionsEnabled, setResourcesActionEnabled] = useState<{ [key: string]: { deployEnabled: boolean, undeployEnabled: boolean, deleteEnabled: boolean } }>();

    const loadData = async () => {
        if (!envDetails) {
            return;
        }

        const [resources, resourcesCommitInfo] = await Promise.allSettled([
            getEnvironmentService().getStageResources(envId!, projectId!),
            fetchResourcesCommitInfo(),
            fetchStageDetails()
        ]);

        if (resources.status === "fulfilled") {
            const outputs = resources.value.data.resources.reduce((acc, resource) => {
                const outputs = resource.component.schema?.properties?.outputs?.properties || {};
                acc[resource.name] = Object.keys(outputs);
                return acc;
            }, {} as Dictionary<string[]>);

            const commitMap: { [resourceName: string]: object } = resourcesCommitInfo.status === "fulfilled" ? resourcesCommitInfo.value : {};

            const mappedResourceActions: { [key: string]: { deployEnabled: boolean, undeployEnabled: boolean, deleteEnabled: boolean } } = {};
            setResources(resources.value.data.resources.map(item => {
                // Set the action buttons status for each resource
                mappedResourceActions[item.name] = {
                    deployEnabled: isResourceActionEnabled({
                        action: "deploy",
                        resourceStatus: item.status,
                        envStatus: envDetails.status!
                    }),
                    undeployEnabled: isResourceActionEnabled({
                        action: "undeploy",
                        resourceStatus: item.status,
                        envStatus: envDetails.status!
                    }),
                    deleteEnabled: isResourceActionEnabled({
                        action: "delete",
                        resourceStatus: item.status,
                        envStatus: envDetails.status!
                    })
                };

                return {
                    ...item,
                    commit: commitMap[item.name]
                };
            }));
            setStageResourcesOutputs(outputs);
            setResourcesActionEnabled(mappedResourceActions);
        }

        setLoading(false);
    }

    useEffect(() => {
        loadData();
        const interval = setInterval(async () => {
            loadData();
        }, 10000);
        return () => clearInterval(interval);
    }, [envDetails]);

    const fetchResourcesCommitInfo = async () => {
        try {
            const { data: { response: stagesDeploymentHistory } } = await getElasticSearchService()
                .getStageDeploymentHistory(projectId!, envId!, envDetails.lastDeploymentId);

            const commitMap: { [serviceName: string]: object } = stagesDeploymentHistory[0].resources
                .reduce((prev, curr) => {
                    return {
                        ...prev,
                        [curr.name]: {
                            repository: curr.component.metadata.repository,
                            message: curr.component.metadata.commit.message,
                            branch: curr.component.metadata.commit.name,
                            version: curr.component.metadata.commit.version,
                            user: curr.component.metadata.commit.user
                        }
                    }
                }, {});
            return commitMap;
        } catch (e) {
            return {};
        }
    }

    const handleUndeployResource = async (resourceName: string) => {
        // check if there are other resources that reference the one that is selected for undeployment
        const deployedResources = Object.keys(resourcesConfigurations!)
            .reduce((acc, rName) => {
                if (!["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(resourcesConfigurations![rName].status)) {
                    acc[rName] = resourcesConfigurations![rName];
                    return acc;
                }
                return acc;
            }, {} as ResourceConfigurationAndStatus);

        const referencedByResources = getResourcesReferencingParticularResource(resourceName, deployedResources);

        openConfirmModal({
            title: "Undeploy resource",
            description:
                <Space direction="vertical">
                    <div>
                        The resource will be undeployed from this environment. This means that it will not consume any resources on AWS but will be available to be redeployed.
                    </div>
                    <div>
                        If you want to completely remove it from this environment, choose <span style={{ fontStyle: 'italic' }}>Delete resource</span> from the same menu.
                    </div>
                    {
                        !!referencedByResources?.length &&
                        <Alert
                            message={
                                <div>
                                    The <b>{resourceName}</b> resource is being referenced by some active resources. If you proceed to <b>Undeploy</b> this resource, the following resources will also be undeployed:
                                    <b> {referencedByResources.join(", ")}.</b>
                                </div>
                            }
                            type="error"
                        />
                    }
                </Space>,
            confirmation: resourceName,
            okText: "Undeploy",
            cancelText: "Cancel",
            onOk: async () => { await undeployResource(resourceName); }
        })
    }

    const handleDeleteResource = async (resourceName: string) => {
        openConfirmModal({
            title: "Delete resource",
            description:
                <Space direction="vertical">
                    <div>
                        The resource will be permanently deleted from this environment.
                    </div>
                    <div>
                        If you just want to undeploy it, choose <span style={{ fontStyle: 'italic' }}>Undeploy resource</span> from the same menu.
                    </div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
            confirmation: resourceName,
            okText: "Delete",
            cancelText: "Cancel",
            onOk: async () => { await deleteResource(resourceName); }
        })
    }

    const deleteResource = async (resourceName: string) => {
        try {
            // we actually dont need the stage to have an AWS account in order to perform this operation, but it is nice for consistency
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // AWS account is connected to both stage and project
                // delete resource
                await getEnvironmentService().deleteResource(envId!, resourceName, projectId!);
                notification.success({
                    message: "Resource deleted",
                    description: `The resource ${resourceName} has been successfully deleted from this environment.`
                });
                trackEnvComponentRemoved(
                    envId!,
                    resource?.component?.id!,
                    resource?.component?.name!,
                );
            } else {
                // state can be updated with a function that returns the current state, so we need to use a function that returns the function we want to set in state
                setExecuteSavedAction(() => () => deleteResource(resourceName));
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (error: any) {
            notification.error({
                message: "Error while trying to delete resource",
                description: error?.response?.data?.message ? error?.response?.data?.message : "We encountered an error while trying to delete this resource. Please try again."
            });
        }
    }

    const undeployResource = async (resourceName: string) => {
        try {
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // AWS account is connected to both stage and project
                // initiate deployment with version NULL for the resource that we want to undeploy
                await getEnvironmentService().deployStage(
                    envId!,
                    projectId!,
                    {
                        partial: true,
                        resourceVersionOverrides: {
                            [resourceName]: null as any
                        }
                    });
                notification.success({
                    message: "Undeploy initiated",
                    description: "It could take a few moments for the changes to take effect."
                });
                trackQuickDeploy(envId!);
                setTimeout(() => {
                    navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                }, 2000);
            } else {
                // state can be updated with a function that returns the current state, so we need to use a function that returns the function we want to set in state
                setExecuteSavedAction(() => () => undeployResource(resourceName));
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (error: any) {
            notification.error({
                message: "Error while trying to undeploy resource",
                description: error?.response?.data?.message ? error?.response?.data?.message : "We encountered an error while trying to undeploy this resource. Please try again."
            });
        }
    }

    const handleDeployResource = async (resource: GetStageResourcesResponseResources) => {
        setResource({
            ...resource,
            // add the resource's configurations as part of the data forwarded
            configurations: resourcesConfigurations?.[resource.name].configurations!
        });
        setShowDeployResourceModal(true);
    }

    const deployStage = async () => {
        try {
            setLoadingDeploy(true);
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // Check if the env needs to be undeployed first
                if (envDetails.status === "REVIEW_IN_PROGRESS" || envDetails.status === "ROLLBACK_COMPLETE") {
                    showUndeployEnvironmentModalConfirm({
                        envId: envId!,
                        envStatus: envDetails?.status,
                        projectId: projectId!,
                        navigate
                    });
                } else {
                    // AWS account is connected to both stage and project
                    await getEnvironmentService().deployStage(envId!, projectId!);
                    notification.info({
                        message: "Deployment in progress",
                        description: "New deployment of the infrastructure was triggered."
                    });
                    trackQuickDeploy(envId!);
                    navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                }
            } else {
                // state can be updated with a function that returns the current state, so we need to use a function that returns the function we want to set in state
                setExecuteSavedAction(() => () => deployStage());
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (err: any) {
            notification.error({
                message: "Error occured",
                description: err.response.data ? err.response.data.message : "Unknown error occured"
            });
        } finally {
            setLoadingDeploy(false);
        }
    }

    /**
     * Fetch the configurations for all resources in stage and store them in state
     * The appropriate resource configuration is forwarded when DeployResourceModal is opened
     */
    const fetchStageDetails = async () => {
        const { data: { resources } } = await getEnvironmentService().getStageDetails(envId!, projectId!);
        const mappedResourcesConfigurations = resources.reduce((prev, curr) => {
            return {
                ...prev,
                [curr.name]: {
                    status: curr.status,
                    configurations: curr.configurations
                }
            }
        }, {});
        setResourcesConfigurations(mappedResourcesConfigurations);
    }

    return (
        <Space direction="vertical" size="large" className="full-width">
            {
                loading ?
                    <Skeleton /> :
                    <>
                        <Row gutter={24}>
                            <Col span={12}>
                                <Title style={{ color: "#244460" }} level={5}>
                                    Resources
                                    <ExplanationButton
                                        content={<ResourceExplanation />}
                                    />
                                </Title>
                            </Col>
                            {!!resources.length &&
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Space>
                                        <Button
                                            disabled={envDetails.status?.endsWith("IN_PROGRESS")}
                                            onClick={() => {
                                                navigate(`/projects/${projectId}/environments/${envId}/infrastructure/resources`);
                                            }}>
                                            <PlusCircleOutlined />
                                            Add resource
                                        </Button>
                                        <Button
                                            loading={loadingDeploy}
                                            disabled={envDetails.status?.endsWith("IN_PROGRESS")}
                                            onClick={() => handleDeployEnvironment({
                                                // currentProjectId: projectId!,
                                                // shouldInitiateTrial,
                                                // setShouldInitiateTrial,
                                                deployStage,
                                                // setCurrentProject,
                                                // updateCurrentProject
                                            })}
                                        >
                                            Deploy all resources
                                        </Button>
                                        {/** TODO: replace the Button with the Dropdown when the 'Review Infrastructure changes' functionality is done */}
                                        {/* <Dropdown
                                            trigger={["click"]}
                                            placement="bottomRight"
                                            disabled={envDetails!.status!.endsWith("IN_PROGRESS")}
                                            overlay={
                                                <Menu>
                                                    <Menu.Item key="deploy" onClick={handleOnDeploy}>
                                                        Deploy all resources
                                                    </Menu.Item>
                                                    <Menu.Item key="review" disabled>
                                                        Review infrastructure changes
                                                    </Menu.Item>
                                                </Menu>
                                            }>
                                            <Button loading={loadingDeploy}>
                                                Trigger deploy <DownOutlined />
                                            </Button>
                                        </Dropdown> */}
                                    </Space>
                                </Col>}
                        </Row>
                        <Table
                            loading={loading}
                            dataSource={resources}
                            pagination={false}
                            className="ant-table-wrapper-bordered"
                            locale={
                                {
                                    emptyText:
                                        <div style={{ textAlign: 'center' }}>
                                            <SearchOutlined style={{ fontSize: 60, verticalAlign: "middle", color: "#244460" }} />
                                            <Title level={5} style={{ color: "black" }}>It looks like you don't have any resources in your environment</Title>
                                            <p style={{ color: "black" }}>Choose from our ready-made components and expand your infrastructure</p>
                                            <div style={{ margin: "auto", width: "50%" }}>
                                                <Divider plain>OR</Divider>
                                            </div>
                                            <p style={{ color: "black" }}>write fully custom infrastructure for your apps</p>
                                            <Button type="primary" onClick={() => {
                                                navigate(`/projects/${projectId}/environments/${envId}/infrastructure/resources`);
                                            }}>
                                                <PlusCircleOutlined />
                                                Add resources
                                            </Button>
                                        </div>
                                }
                            }
                            footer={() => resources.length ? `Total of ${resources.length} resource(s) in this environment` : "A resource is a building block that creates infrastructure in your cloud environment."}
                            columns={[
                                {
                                    title: 'Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    render: name => <Link to={`/projects/${projectId}/environments/${envId}/resources/${name}`}>
                                        <b>{name}</b>
                                    </Link>
                                },
                                {
                                    title: 'Component',
                                    dataIndex: 'component',
                                    key: 'component',
                                    render: component => component.name
                                },
                                {
                                    title: 'Commit',
                                    key: 'commit',
                                    render: resource => resource.commit && !["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(resource.status) ?
                                        <div style={{ fontSize: "12px" }}>
                                            <b>{resource.commit.message}</b>
                                            <div>
                                                Commit by <Avatar src={resource.commit.user?.avatar} size={16} /> {resource.commit.user?.name}
                                            </div>
                                            <div>
                                                <GitCommitLink version={resource.commit.version} repoUrl={resource.commit.repository} branch={resource.commit.branch} />
                                            </div>
                                        </div> :
                                        <>No deployment information available</>
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: status => <DeploymentStatusTag status={status} />
                                },
                                {
                                    title: 'Actions',
                                    key: 'actions',
                                    align: 'right',
                                    render: resource => (
                                        <Dropdown
                                            placement="bottomRight"
                                            trigger={["click"]}
                                            overlay={(
                                                <Menu items={[
                                                    {
                                                        key: "deploy",
                                                        label: "Deploy resource",
                                                        disabled: !resourceActionsEnabled?.[resource.name].deployEnabled,
                                                        onClick: () => { handleDeployResource(resource) }
                                                    },
                                                    {
                                                        key: "undeploy",
                                                        label: <MenuItemLabel
                                                            text="Undeploy resource"
                                                            showTooltip={!resourceActionsEnabled?.[resource.name].undeployEnabled}
                                                            tooltipText="Resource must be deployed in order to be able to perform this action"
                                                        />,
                                                        danger: resourceActionsEnabled?.[resource.name].undeployEnabled,
                                                        disabled: !resourceActionsEnabled?.[resource.name].undeployEnabled,
                                                        onClick: () => { handleUndeployResource(resource.name) }
                                                    },
                                                    {
                                                        key: "delete",
                                                        label: <MenuItemLabel
                                                            text="Delete resource"
                                                            showTooltip={!resourceActionsEnabled?.[resource.name].deleteEnabled}
                                                            tooltipText="Resource must be undeployed first in order to be able to perform this action"
                                                        />,
                                                        danger: resourceActionsEnabled?.[resource.name].deleteEnabled,
                                                        disabled: !resourceActionsEnabled?.[resource.name].deleteEnabled,
                                                        onClick: () => { handleDeleteResource(resource.name) }
                                                    }
                                                ]} />
                                            )}
                                        >
                                            <Button icon={<MoreOutlined />} />
                                        </Dropdown>

                                    )
                                }
                            ]}
                            rowKey="name"
                        />
                        <br />
                    </>
            }

            {
                <StageClustersTable
                    projectId={projectId!}
                    envDetails={envDetails}
                    setShowConnectClusterModal={setShowConnectClusterModal}
                />
            }

            {
                showConnectClusterModal &&
                <ConnectClusterModal
                    envId={envId}
                    visible={showConnectClusterModal}
                    onCancel={() => setShowConnectClusterModal(false)}
                    onOk={() => {
                        loadData();
                        setShowConnectClusterModal(false);
                    }}
                />
            }
            {
                // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'connectAwsAccountModalVisible'
                // We have a process of periodic API calls which remain active even if when the modal closes
                connectAwsAccountModalVisible &&
                <ConnectAwsAccountModal
                    visible={connectAwsAccountModalVisible}
                    onOk={async (newAwsAccountId) => {
                        setConnectAwsAccountModalVisible(false);
                        setAwsAccountId(newAwsAccountId);
                        setChooseAwsAccountModal(true);
                    }}
                    onCancel={() => setConnectAwsAccountModalVisible(false)}
                />
            }
            {
                // This modal becomes active when the user connects an AWS account and now needs to select a region for the stage
                chooseAwsAccountModal &&
                <ChooseAwsAccountModal
                    selectedAccount={{ accountId: awsAccountId, region: undefined }}
                    visible={chooseAwsAccountModal}
                    onCancel={() => setChooseAwsAccountModal(false)}
                    onOk={async ({ accountId, region }: { accountId: string, region: string }) => {
                        await linkAwsAccountToStage({
                            envId: envId!,
                            projectId: projectId!,
                            awsAccountId: accountId,
                            awsRegion: region
                        });
                        setChooseAwsAccountModal(false);
                        executeSavedAction?.();
                    }}
                />
            }
            {
                showDeployResourceModal &&
                <DeployResourceModal
                    visible={showDeployResourceModal}
                    resource={resource!}
                    stageResourcesOutputs={stageResourcesOutputs}
                    notDeployedResources={
                        resources
                            .filter(r => ["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(r.status))
                            .map(r => r.name)
                    }
                    onCancel={() => setShowDeployResourceModal(false)}
                    onOk={() => {
                        setShowDeployResourceModal(false);
                        setTimeout(() => {
                            navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                        }, 2000);
                    }}
                />
            }
        </Space >
    );
}

export default EnvironmentInfrastructure;